import { useState } from 'react';
import { Search, ShieldAlert, TriangleAlert } from 'lucide-react';
import {
  Button,
  Card,
  DataTable,
  DateRangeFilter,
  EmptyState,
  ErrorState,
  ExportButton,
  LoadingState,
  NotAvailable,
  Pager,
  PanelTitle,
  Select,
  Stat,
  StatusPill,
  TextField,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { saveBlob, exportFilename } from '../../lib/api/download';
import { formatCents, formatCount } from '../../lib/format/money';
import { formatDate, type DayRange } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import type { OrderSearchParams, OrderSearchRow } from '../orders/api';
import { DetailDialogs, useDetailLinks } from '../orders/DetailDialogs';
import { useOrderSearch } from '../orders/queries';
import { dayRangeParam, humanize, listParam, orderStatusTone, pickParam, stringParam } from '../profiles/people';
import { LocalePicker } from '../locales/LocalePicker';
import { affectedEatersCsv, type RecallAffectedOrder, type RecallBy } from './api';
import { useRecallQuery } from './queries';

type SearchBy = 'byOrderId' | 'byListingId' | 'byDishName' | 'byChefId' | 'byChefName';
const SEARCH_BY: Array<[SearchBy, string]> = [
  ['byOrderId', 'Order number or ID'],
  ['byListingId', 'Listing ID'],
  ['byDishName', 'Dish name'],
  ['byChefId', 'Chef ID'],
  ['byChefName', 'Chef name'],
];
const SEARCH_PLACEHOLDER: Record<SearchBy, string> = {
  byOrderId: 'e.g. ORD-325001',
  byListingId: 'Paste a listing ID',
  byDishName: 'e.g. lasagna',
  byChefId: 'Paste a chef ID',
  byChefName: 'e.g. Gretchen',
};
const SEARCH_KEYS = SEARCH_BY.map(([k]) => k);

const RECALL_BY: Array<[RecallBy, string]> = [
  ['listingId', 'Listing ID'],
  ['dishName', 'Dish name'],
  ['chefProfileId', 'Chef ID'],
  ['chefName', 'Chef name'],
  ['ingredientTag', 'Ingredient or tag'],
];
const RECALL_PLACEHOLDER: Record<RecallBy, string> = {
  listingId: 'Paste a listing ID',
  dishId: 'Paste a dish ID',
  dishName: 'e.g. lasagna',
  chefProfileId: 'Paste a chef ID',
  chefName: 'e.g. Gretchen',
  ingredientTag: 'e.g. peanut',
};
const RECALL_KEYS = RECALL_BY.map(([k]) => k);

/**
 * Traceability & Recall (kit `TraceabilityScreen`; FE plan §7). Search-first:
 * `/admin/orders/search` needs one identifier, so nothing loads until one is
 * given. "Who is affected" runs `/admin/recall/query` for a dish, listing,
 * chef or ingredient and exports the affected eaters. Notifying them and a
 * recall log are not built yet. Every input lives in the URL.
 */
export function TraceabilityScreen() {
  const table = useTableUrlState();
  const localeIds = listParam(table.filters.localeIds);
  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-semibold text-muted">Locales</span>
        <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
      </div>
      <OrderLookup localeIds={localeIds} />
      <WhoIsAffected localeIds={localeIds} />
      <DetailDialogs />
    </div>
  );
}

// --- order lookup -------------------------------------------------------------------

function lookupColumns(
  openOrder: (id: string) => void,
  openDish: (id: string) => void,
  onAffected: (listingId: string) => void,
): DataTableColumn<OrderSearchRow>[] {
  return [
    {
      id: 'order',
      header: 'Order',
      width: '130px',
      cell: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openOrder(r.orderId);
          }}
          className="border-0 bg-transparent p-0 font-mono text-[13px] font-semibold text-ink underline decoration-dotted underline-offset-2 hover:text-orange"
        >
          {r.orderNumber}
        </button>
      ),
    },
    {
      id: 'dish',
      header: 'Dish',
      width: '1.6fr',
      cell: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation(); // the row opens the order; the dish name opens the dish
            openDish(r.listingId);
          }}
          className="truncate border-0 bg-transparent p-0 text-left font-semibold text-ink hover:text-orange"
        >
          {r.dishTitle || 'Untitled'}
        </button>
      ),
    },
    {
      id: 'chef',
      header: 'Chef',
      width: '1.2fr',
      cell: (r) => <span className="truncate text-muted">{r.chefDisplayName}</span>,
    },
    {
      id: 'eater',
      header: 'Eater',
      width: '1.2fr',
      cell: (r) => <span className="truncate text-muted">{r.eaterDisplayName}</span>,
    },
    {
      id: 'date',
      header: 'Ordered',
      width: '110px',
      cell: (r) => <span className="text-muted">{formatDate(r.orderedAt)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '136px',
      cell: (r) => <StatusPill tone={orderStatusTone(r.status)}>{humanize(r.status)}</StatusPill>,
    },
    { id: 'qty', header: 'Qty', width: '48px', align: 'right', numeric: true, cell: (r) => formatCount(r.quantity) },
    {
      id: 'total',
      header: 'Total',
      width: '84px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCents(r.totalPaid),
    },
    {
      id: 'affected',
      header: '',
      width: '136px',
      align: 'right',
      action: true,
      cell: (r) => (
        <Button
          variant="ghost-dark"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAffected(r.listingId);
          }}
        >
          Who's affected
        </Button>
      ),
    },
  ];
}

function OrderLookup({ localeIds }: { localeIds: string[] }) {
  const table = useTableUrlState();
  const { openOrder, openDish } = useDetailLinks();
  const by = pickParam(table.filters.by, SEARCH_KEYS) ?? 'byOrderId';
  const id = stringParam(table.filters.id) ?? '';

  const params: OrderSearchParams = { [by]: id, localeIds, page: table.page, pageSize: table.pageSize };
  const search = useOrderSearch(params, { enabled: id.length > 0 });

  const columns = lookupColumns(openOrder, openDish, (listingId) =>
    table.setParams({ rby: 'listingId', rv: listingId }),
  );

  return (
    <Card surface="panel">
      <PanelTitle eyebrow="Traceability">Find orders</PanelTitle>
      {/* Keyed on the URL values so the inputs reset to them after a search or back/forward. */}
      <LookupForm
        key={`${by}|${id}`}
        by={by}
        id={id}
        onSearch={(nextBy, nextId) => table.setParams({ by: nextBy, id: nextId || undefined, page: undefined })}
      />

      {!id ? (
        <EmptyState compact body="Search by order number, dish, listing or chef to trace orders." />
      ) : (
        <>
          <DataTable
            label="Orders"
            minWidth={1100}
            columns={columns}
            rows={search.data?.items ?? []}
            getRowId={(r) => r.orderId}
            onRowClick={(r) => openOrder(r.orderId)}
            loading={search.isPending}
            error={search.error}
            onRetry={() => void search.refetch()}
            emptyLabel="No orders match that identifier."
          />
          <Pager
            page={table.page}
            pageSize={table.pageSize}
            total={search.data?.total ?? 0}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
            noun="orders"
          />
        </>
      )}
    </Card>
  );
}

function LookupForm({ by, id, onSearch }: { by: SearchBy; id: string; onSearch: (by: SearchBy, id: string) => void }) {
  const [draft, setDraft] = useState(id);
  const [draftBy, setDraftBy] = useState<SearchBy>(by);
  const submit = () => onSearch(draftBy, draft.trim());
  return (
    <form
      className="mb-4 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Select
        aria-label="Search by"
        surface="white"
        compact
        style={{ width: 200 }}
        value={draftBy}
        options={SEARCH_BY.map(([value, label]) => ({ value, label }))}
        onChange={(e) => setDraftBy(e.target.value as SearchBy)}
      />
      <TextField
        aria-label="Identifier"
        surface="white"
        placeholder={SEARCH_PLACEHOLDER[draftBy]}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        containerClassName="w-[320px] [&_input]:py-2 [&_input]:text-sm"
      />
      <Button type="submit" size="sm">
        <Search size={15} aria-hidden="true" />
        Search
      </Button>
    </form>
  );
}

// --- who is affected ------------------------------------------------------------------

const AFFECTED_COLUMNS: DataTableColumn<RecallAffectedOrder>[] = [
  {
    id: 'order',
    header: 'Order',
    width: '120px',
    cell: (r) => <span className="font-mono text-[13px]">{r.orderNumber}</span>,
  },
  {
    id: 'dish',
    header: 'Dish',
    width: '1.4fr',
    cell: (r) => <span className="truncate font-semibold">{r.dishTitle}</span>,
  },
  {
    id: 'chef',
    header: 'Chef',
    width: '1.1fr',
    cell: (r) => <span className="truncate text-muted">{r.chefName}</span>,
  },
  { id: 'eater', header: 'Eater', width: '1.1fr', cell: (r) => <span className="truncate">{r.eaterName}</span> },
  {
    id: 'email',
    header: 'Email',
    width: '1.4fr',
    cell: (r) => <span className="truncate text-muted">{r.eaterEmail ?? '—'}</span>,
  },
  {
    id: 'date',
    header: 'Ordered',
    width: '104px',
    cell: (r) => <span className="text-muted">{formatDate(r.orderedAt)}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    width: '136px',
    cell: (r) => <StatusPill tone={orderStatusTone(r.status)}>{humanize(r.status)}</StatusPill>,
  },
  { id: 'qty', header: 'Qty', width: '48px', align: 'right', numeric: true, cell: (r) => formatCount(r.quantity) },
];

function WhoIsAffected({ localeIds }: { localeIds: string[] }) {
  const table = useTableUrlState();
  const { openOrder } = useDetailLinks();
  const { profile } = useSession();
  const f = table.filters;
  const by = pickParam(f.rby, RECALL_KEYS) ?? 'listingId';
  const value = stringParam(f.rv) ?? '';
  const days = dayRangeParam(f.rfrom, f.rto);

  const recall = useRecallQuery(value ? { by, value, from: days.start, to: days.end, localeIds } : null);
  const data = recall.data;

  return (
    <Card surface="panel">
      <PanelTitle eyebrow="Recall">Who is affected</PanelTitle>
      <RecallForm
        key={`${by}|${value}|${days.start ?? ''}|${days.end ?? ''}`}
        by={by}
        value={value}
        days={days}
        timeZone={profile?.timezone}
        onFind={(nextBy, nextValue, nextDays) =>
          table.setParams({ rby: nextBy, rv: nextValue || undefined, rfrom: nextDays.start, rto: nextDays.end })
        }
      />

      {!value ? (
        <EmptyState
          compact
          body="Pick a dish, listing, chef or ingredient to see every order and eater it reached. Canceled and expired orders are left out."
        />
      ) : recall.isPending ? (
        <LoadingState label="Finding affected orders…" />
      ) : recall.isError ? (
        <ErrorState error={recall.error} onRetry={() => void recall.refetch()} compact />
      ) : data ? (
        <>
          <div className="mb-3.5 grid grid-cols-4 gap-2.5">
            <Stat label="Orders" value={formatCount(data.result.orderCount)} />
            <Stat label="Portions" value={formatCount(data.result.portionCount)} />
            <Stat label="Eaters" value={formatCount(data.result.eaterCount)} />
            <Stat label="Chefs" value={formatCount(data.result.chefCount)} />
          </div>
          {data.warnings.map((w) => (
            <div
              key={w}
              className="mb-3.5 flex gap-2 rounded-[10px] border border-[rgba(180,158,49,.34)] bg-[rgba(180,158,49,.14)] px-3.5 py-2.5 text-[13px]"
            >
              <TriangleAlert size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-[#7A5A12]" />
              {w}
            </div>
          ))}
          <div className="mb-3 flex justify-end">
            <ExportButton
              label="Export affected eaters"
              onExport={async () => {
                saveBlob(
                  new Blob([affectedEatersCsv(data.result.affectedOrders)], { type: 'text/csv' }),
                  exportFilename('recall-affected-eaters', 'csv'),
                );
              }}
            />
          </div>
          <DataTable
            label="Affected orders"
            compact
            minWidth={1040}
            columns={AFFECTED_COLUMNS}
            rows={data.result.affectedOrders}
            getRowId={(r) => r.orderId}
            onRowClick={(r) => openOrder(r.orderId)}
            emptyLabel="No orders reached anyone for that scope."
          />
          <div className="mt-3.5">
            <NotAvailable
              title="Notify & recall log"
              reason="Notifying affected eaters and keeping a recall log aren't built yet."
            />
          </div>
        </>
      ) : null}
    </Card>
  );
}

function RecallForm({
  by,
  value,
  days,
  timeZone,
  onFind,
}: {
  by: RecallBy;
  value: string;
  days: DayRange;
  timeZone?: string;
  onFind: (by: RecallBy, value: string, days: DayRange) => void;
}) {
  const [draftBy, setDraftBy] = useState<RecallBy>(by);
  const [draft, setDraft] = useState(value);
  const [draftDays, setDraftDays] = useState(days);
  const submit = () => onFind(draftBy, draft.trim(), draftDays);
  return (
    <form
      className="mb-4 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Select
        aria-label="Affected by"
        surface="white"
        compact
        style={{ width: 190 }}
        value={draftBy}
        options={RECALL_BY.map(([v, label]) => ({ value: v, label }))}
        onChange={(e) => setDraftBy(e.target.value as RecallBy)}
      />
      <TextField
        aria-label="Recall scope"
        surface="white"
        placeholder={RECALL_PLACEHOLDER[draftBy]}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        containerClassName="w-[280px] [&_input]:py-2 [&_input]:text-sm"
      />
      <DateRangeFilter value={draftDays} timeZoneLabel={timeZone} onChange={setDraftDays} />
      <Button type="submit" size="sm">
        <ShieldAlert size={15} aria-hidden="true" />
        Find affected
      </Button>
    </form>
  );
}
